import type { Language } from '../i18n';

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;
const TB = GB * 1024;

/** Human-readable byte size. Locale-aware decimal separator. */
export function formatBytes(bytes: number, lang: Language = 'de'): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const nf = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 });
  if (bytes >= TB) return `${nf.format(bytes / TB)} TB`;
  if (bytes >= GB) return `${nf.format(bytes / GB)} GB`;
  if (bytes >= MB) return `${nf.format(bytes / MB)} MB`;
  if (bytes >= KB) return `${nf.format(bytes / KB)} KB`;
  return `${bytes} B`;
}

export function formatDateTime(iso: string, lang: Language = 'de'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(lang, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

export function formatDate(iso: string, lang: Language = 'de'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(lang, { dateStyle: 'medium' }).format(d);
}

export function formatEUR(amount: number, lang: Language = 'de'): string {
  return new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(amount) ? amount : 0,
  );
}

export function formatNumber(n: number, lang: Language = 'de'): string {
  return new Intl.NumberFormat(lang).format(Number.isFinite(n) ? n : 0);
}

/** Short middle-truncated hash for display; full value lives in title/copy. */
export function shortHash(hash: string, head = 10): string {
  if (!hash) return '—';
  return hash.length <= head + 4 ? hash : `${hash.slice(0, head)}…`;
}
